import React from 'react';
import { CSSTransition } from 'react-transition-group';
import CustomInput from '../CustomInput/CustomInput';
import DMSpinnerButton from '../../common/DMSpinnerButton/DMSpinnerButton';
import CheckIcon from '../../../assets/Icons/check.svg';
import ErrorIcon from '../../../assets/Icons/error.svg';
import APIService from '../../../services/APIService';
import './restore-password-form.scss';

const RestorePasswordForm = ({ hash, onBack }) => {
    const passwordRef = React.useRef();
    const confirmPasswordRef = React.useRef();
    const [values, setValues] = React.useState({
        password: '',
        confirmPassword: ''
    });
    const [touched, setTouched] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [revealPassword, setRevealPassword] = React.useState(false);
    const [revealConfirmPassword, setRevealConfirmPassword] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [passwordChanged, setPasswordChanged] = React.useState(false);
    const [apiError, setApiError] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');

    const validatePassword = (password) => {
        return {
            minLength: password.trim().length >= 8,
            hasDigit: /\d/.test(password),
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };
    };

    const isPasswordValid = (password) => {
        const validation = validatePassword(password);
        return Object.values(validation).every(v => v === true);
    };

    const passwordValidation = validatePassword(values.password);

    const getRequirementClass = (password, isValid) => {
        if (!password) return 'pending';
        return isValid ? 'valid' : 'invalid';
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'password' || name === 'confirmPassword') {
            const password = name === 'password' ? value : values.password;
            const confirmPassword = name === 'confirmPassword' ? value : values.confirmPassword;

            if (confirmPassword && password !== confirmPassword) {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: 'Passwords do not match'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    confirmPassword: undefined
                }));
            }
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));

        if (name === 'confirmPassword' && values.password !== values.confirmPassword) {
            setErrors(prev => ({
                ...prev,
                confirmPassword: 'Passwords do not match'
            }));
        }
    };

    const handleRevealPasswordClicked = () => {
        setRevealPassword(prevState => !prevState);
    };

    const handleRevealConfirmPasswordClicked = () => {
        setRevealConfirmPassword(prevState => !prevState);
    };

    const handleSubmit = async () => {
        if (!isPasswordValid(values.password)) {
            return;
        }

        setLoading(true)
        if (values.password !== values.confirmPassword) {
            setErrors(prev => ({
                ...prev,
                confirmPassword: 'Passwords do not match'
            }));
            return;
        }

        var payload = getChangePassportQueryWithVars();

        try {
            const res = await APIService.post('https://passport-api.services.r2net.com/auth', payload);

            if (res?.data?.changePassword?.status) {
                setPasswordChanged(true);
                setApiError('');
                setSuccessMessage(res?.data?.changePassword?.message || 'Password reset successfully!');
            } else {
                setApiError(res?.data?.changePassword?.message || 'Failed to reset password');
                setSuccessMessage('');
                console.log("Failed to reset password", res.data.changePassword.message);
            }
            setLoading(false);

        } catch (error) {
            console.log("Failed to reset password", error);
            setApiError('An error occurred. Please try again later.');
            setSuccessMessage('');
            setLoading(false);
        }
    };


    const getChangePassportQueryWithVars = () => {
        return {
            query: `
                query($hash: String, $newPassword: String!){
                        changePassword(
                        hash: $hash,
                                    newPassword: $newPassword
                        ){
                    status,
                    message
                    }
                }
            `,
            variables: {
                hash: hash,
                newPassword: values.password.trim()
            }
        };

    }

    const getButtonText = () => {
        if (loading) return '';
        if (passwordChanged) return 'Back to Login';
        return 'Reset Password';
    };

    return (
        <div className={passwordChanged ? 'restore-passport-container pass-changed' : 'restore-passport-container'}>
            <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                <div className='form-header transition-delay'>
                    <span className='login-txt'>Reset Your Password</span>
                </div>
            </CSSTransition>

            <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                <CustomInput
                    inputRef={passwordRef}
                    handleChange={handleChange}
                    className='password-i transition-delay'
                    type='password'
                    label='Password'
                    name='password'
                    placeholder='Your Password'
                    value={values.password}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    revealPassword={revealPassword}
                    onRevealPasswordClick={handleRevealPasswordClicked}
                />
            </CSSTransition>

            {!passwordChanged && (
                <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                    <div className='password-requirements'>
                        <span className={`requirement-chip ${getRequirementClass(values.password, passwordValidation.minLength)}`}>8+ chars</span>
                        <span className={`requirement-chip ${getRequirementClass(values.password, passwordValidation.hasDigit)}`}>1 digit</span>
                        <span className={`requirement-chip ${getRequirementClass(values.password, passwordValidation.hasUppercase)}`}>1 uppercase</span>
                        <span className={`requirement-chip ${getRequirementClass(values.password, passwordValidation.hasLowercase)}`}>1 lowercase</span>
                        <span className={`requirement-chip ${getRequirementClass(values.password, passwordValidation.hasSpecial)}`}>1 special</span>
                    </div>
                </CSSTransition>
            )}

            <CSSTransition in={true} timeout={8500} classNames="form-elem-transition sssss" appear>
                <CustomInput
                    inputRef={confirmPasswordRef}
                    handleChange={handleChange}
                    className='password-i transition-delay'
                    type='password'
                    label='Confirm Password'
                    name='confirmPassword'
                    placeholder='Confirm Your Password'
                    value={values.confirmPassword}
                    handleBlur={handleBlur}
                    errors={errors}
                    touched={touched}
                    revealPassword={revealConfirmPassword}
                    onRevealPasswordClick={handleRevealConfirmPasswordClicked}
                />
            </CSSTransition>

            {!passwordChanged && (
                <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                    <div className='access-your-account-section transition-delay back-to-login'>
                        <a
                            href='#'
                            onClick={(e) => { e.preventDefault(); onBack && onBack(); }}
                            className='reset-link-txt'
                        >
                            Back to Login
                        </a>
                    </div>
                </CSSTransition>
            )}

            {successMessage && (
                <div className='success-message'>
                    <img src={CheckIcon} alt="" />
                    <span className='success-text'>{successMessage}</span>
                </div>
            )}

            {apiError && (
                <div className='api-error-message'>
                    <img src={ErrorIcon} alt="" />
                    <span className='error-text'>{apiError}</span>
                </div>
            )}

            <CSSTransition in={true} timeout={8500} classNames="form-elem-transition" appear>
                <div className='btn-container transition-delay'>
                    <DMSpinnerButton
                        onClick={passwordChanged ? onBack : handleSubmit}
                        className={`login-btn ${loading && 'disabled'}`}
                        text={getButtonText()}
                        type={'submit'}
                        disabled={passwordChanged ? false : (loading || !values.password || !values.confirmPassword || values.password !== values.confirmPassword || !isPasswordValid(values.password))}
                        transitionIn={loading}
                        transitionClassName={'login-anim'}
                        withTransition={true} />
                </div>
            </CSSTransition>
        </div>
    );
};

export default RestorePasswordForm;